import type {
  AreaBoundaryRings,
  DestinationFormValue,
  DestinationTranslationValue,
  IAreaBoundary,
  IDestination,
} from '@/shared/api/types/destination-types'
import { DEFAULT_DESTINATION_FORM } from '@/shared/constants/destination-form'

/** The API sends the polygon as bare rings; the form works with a GeoJSON-like polygon. Empty means "no boundary". */
export function areaBoundaryFromRings(
  rings: AreaBoundaryRings | null | undefined,
): IAreaBoundary | null {
  return rings && rings.length > 0 ? { type: 'Polygon', coordinates: rings } : null
}

/** Form values of an existing destination. */
export function deriveFormFromDestination(destination: IDestination): DestinationFormValue {
  return {
    countryId: destination.countryId,
    provinceId: destination.provinceId,
    slug: destination.slug,
    destinationType: destination.destinationType ?? '',
    areaBoundary: areaBoundaryFromRings(destination.areaBoundary),
    centerPoint: destination.centerPoint ?? null,
    banner: destination.banner ?? '',
    thumbnail: destination.thumbnail ?? '',
    isActive: destination.isActive,
  }
}

/** Form values restored from a saved create-draft; anything the draft lacks falls back to the default. */
export function deriveFormFromDraft(
  draftForm: Partial<DestinationFormValue> | undefined,
): DestinationFormValue {
  const defaults = DEFAULT_DESTINATION_FORM
  return {
    countryId: draftForm?.countryId ?? defaults.countryId,
    provinceId: draftForm?.provinceId ?? defaults.provinceId,
    slug: draftForm?.slug ?? defaults.slug,
    destinationType: draftForm?.destinationType ?? defaults.destinationType,
    areaBoundary: draftForm?.areaBoundary ?? defaults.areaBoundary,
    centerPoint: draftForm?.centerPoint ?? defaults.centerPoint,
    banner: draftForm?.banner ?? defaults.banner,
    thumbnail: draftForm?.thumbnail ?? defaults.thumbnail,
    isActive: draftForm?.isActive ?? defaults.isActive,
  }
}

/** Translation rows restored from a saved create-draft, with missing texts filled with empty strings. */
export function deriveTranslationsFromDraft(
  rows: DestinationTranslationValue[] | undefined,
): DestinationTranslationValue[] {
  return (rows ?? []).map((row) => ({
    language: row.language,
    title: row.title ?? '',
    name: row.name ?? '',
    description: row.description ?? '',
  }))
}
